import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export function CtaSection({ section, base = "" }) {
  return (
    <section className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20 py-24" data-testid="cta-section">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="relative overflow-hidden rounded-sm border border-brass/15 bg-white px-8 py-16 sm:px-16 text-center"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.08),transparent_60%)]" />
        <h2 className="relative text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-ink tracking-tight">{section.title}</h2>
        {section.text && <p className="relative mt-4 text-base text-ink2 max-w-xl mx-auto leading-relaxed">{section.text}</p>}
        {section.button_label && (
          <Link to={`${base}${section.button_link || "/contact"}`} data-testid="cta-button" className="relative mt-8 inline-block rounded-full bg-brass px-10 py-4 text-sm font-semibold uppercase tracking-widest text-cast transition-colors hover:bg-flame hover:text-ink">
            {section.button_label}
          </Link>
        )}
      </motion.div>
    </section>
  );
}

export function RichText({ section }) {
  return (
    <section className="mx-auto max-w-3xl px-6 sm:px-12 py-24" data-testid="richtext-section">
      {section.eyebrow && <p className="text-xs uppercase font-mono tracking-[0.3em] text-gold">{section.eyebrow}</p>}
      {section.title && <h2 className="mt-4 text-3xl sm:text-4xl font-serif font-semibold text-ink">{section.title}</h2>}
      <div className="mt-8 space-y-5 text-base sm:text-lg text-ink2 leading-relaxed [&_p]:leading-relaxed" dangerouslySetInnerHTML={{ __html: section.body || "" }} />
    </section>
  );
}

export function Gallery({ section }) {
  return (
    <section className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20 py-16" data-testid="gallery-section">
      {section.eyebrow && <p className="text-xs uppercase font-mono tracking-[0.3em] text-gold mb-8">{section.eyebrow}</p>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {(section.images || []).map((src, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className={`overflow-hidden rounded-sm border border-brass/10 ${i % 3 === 0 ? "row-span-2" : ""}`}>
            <img src={src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src} alt={`Chef Blaine Guillot gallery ${i + 1}`} className="h-full w-full object-cover aspect-[3/4] transition-transform duration-700 hover:scale-105 max-h-[80vh]" />
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export function InfoSection({ section }) {
  return (
    <section className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20 pb-24" data-testid="info-section">
      {section.eyebrow && <p className="text-xs uppercase font-mono tracking-[0.3em] text-gold mb-8">{section.eyebrow}</p>}
      <div className="grid gap-6 md:grid-cols-3">
        {(section.items || []).map((item, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="rounded-sm border border-brass/10 bg-white p-8" data-testid={`info-card-${i}`}>
            <h3 className="font-serif text-xl text-ink">{item.title}</h3>
            <p className="mt-3 text-sm text-ink2 leading-relaxed">{item.text}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
